import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { FadeIn } from '../components/FadeIn';
const preamble = ['We establish this Charter in recognition that no civilization can remain stable when power is detached from judgment, when institutions are ruled by spectacle rather than competence, and when public life becomes vulnerable to manipulation, volatility, and systemic cognitive decline.', 'We therefore constitute the Cognarchist State as a neuro-sovereign constitutional order founded upon the protection of judgment as a public good, the cultivation of civic competence, the lawful alignment of authority with responsibility, and the development of a high-fidelity civilization capable of enduring beyond the noise of the age.', 'The purpose of the State shall be not merely to govern conduct, but to preserve civilizational coherence, defend signal integrity, restore damaged autonomy where possible, and create the institutional conditions under which self-mastery, truth-responsiveness, and long-range stewardship may flourish.', 'In this spirit, we ordain and establish this Founding Charter for the Cognarchist State.'];
const parts = [{
  numeral: 'I',
  title: 'FOUNDING CHARTER',
  articles: [{
    numeral: 1,
    heading: 'Nature of the State',
    paragraphs: ['The Cognarchist State shall be a constitutional neuro-sovereign republic.', 'The State shall be founded upon the principles of cognitive sovereignty, institutional coherence, lawful developmental governance, and the public protection of sound judgment.', 'Judgment shall be recognized as a constitutional good essential to liberty, order, and long-term prosperity.']
  }, {
    numeral: 2,
    heading: 'Purpose of the State',
    paragraphs: ['The State shall exist:'],
    clauses: ['to preserve public order through competent and reality-based governance;', 'to cultivate the autonomy, discipline, and competence of the citizenry;', 'to defend society against manipulation, cognitive capture, and institutional decay;', 'to align civic influence and institutional access with demonstrated readiness for responsibility; and', 'to advance a civilization grounded in self-mastery rather than spectacle.']
  }, {
    numeral: 3,
    heading: 'The Social Contract',
    paragraphs: ['The social contract of the Cognarchist State shall consist in reciprocal obligation between citizen and State.', 'The State shall provide protection, developmental opportunity, lawful order, civic infrastructure, and restorative institutions.', 'The citizen shall owe duties of lawful conduct, self-development, institutional fidelity, and participation in the maintenance of civic coherence.', 'Freedom under this Charter shall not be understood as the unchecked expression of impulse, but as the verified capacity for autonomous and responsible civic participation.']
  }, {
    numeral: 4,
    heading: 'Constitutional Order of Judgment',
    paragraphs: ['The State shall treat sound judgment as a public good.', 'The cultivation, measurement, and protection of judgment shall be lawful functions of the constitutional order.', 'No office of strategic consequence shall be detached from standards of competence, emotional stability, reality testing, and responsibility.']
  }]
}, {
  numeral: 'II',
  title: 'BILL OF RIGHTS AND CIVIC GUARANTEES',
  articles: [{
    numeral: 5,
    heading: 'Basic Guarantees of Personhood',
    paragraphs: ['Every person within the jurisdiction of the State shall be entitled to:'],
    clauses: ['bodily security;', 'protection from arbitrary violence or extrajudicial punishment;', 'access to essential sustenance and minimum civic protection;', 'recognition of basic human dignity; and', 'restoration-oriented treatment where impairment is alleged.'],
    afterClauses: 'No civic ranking, evaluative status, or institutional tier shall extinguish these guarantees.'
  }, {
    numeral: 6,
    heading: 'Rights of Process and Review',
    paragraphs: ['No citizen shall suffer material deprivation of civic standing, institutional access, or strategic eligibility without notice of the grounds.', 'Every citizen shall have the right to review, appeal, and procedural challenge of adverse determinations.', 'Evaluative systems shall be auditable and reviewable under law.', 'No hidden or purely secret mechanism shall be the sole basis for serious civic disability.']
  }, {
    numeral: 7,
    heading: 'Right to Restoration',
    paragraphs: ['Any citizen found to be impaired in judgment stability, emotional regulation, or autonomous functioning shall retain the right to restoration where recovery is reasonably achievable.', 'Recalibration shall be restorative rather than inherently punitive.', 'No citizen shall be declared permanently unfit without the gravest lawful grounds and full constitutional process.']
  }, {
    numeral: 8,
    heading: 'Conditional Access to High-Stakes Functions',
    paragraphs: ['Access to high-trust offices, command roles, security functions, strategic finance, and other consequential positions may be conditioned upon verified competence and continued readiness.', 'Such conditions shall be lawful, reviewable, and proportionate to the responsibility of the office.', 'The Constitution shall distinguish between basic rights of personhood and conditional access to high-consequence power.']
  }, {
    numeral: 9,
    heading: 'Protection Against Arbitrary Rule',
    paragraphs: ['No authority of the State may impose tier degradation, emergency sanction, or institutional exclusion arbitrarily.', 'All serious exercises of public power shall be grounded in law, documented reason, and constitutional review.', 'Political spectacle, popularity, or rhetorical pressure shall not constitute lawful grounds for deprivation.']
  }, {
    numeral: 10,
    heading: 'Duty of Civic Fidelity',
    paragraphs: ['Every citizen owes a duty:'],
    clauses: ['to preserve his or her own capacity for sound judgment;', 'to refrain from reckless participation in institutional sabotage or manipulative civic disorder;', 'to comply with lawful restorative and evaluative processes; and', 'to uphold the continuity of the constitutional order.']
  }]
}, {
  numeral: 'III',
  title: 'THE NEUROCOGNITIVE CIVIC JUDGMENT INDEX',
  articles: [{
    numeral: 11,
    heading: 'Establishment of the NCJI',
    paragraphs: ['There shall be established a constitutional evaluative framework known as the Neurocognitive Civic Judgment Index (NCJI).', 'The NCJI shall serve as the principal instrument for assessing dimensions of judgment relevant to civic trust, leadership, developmental guidance, and constitutional readiness.']
  }, {
    numeral: 12,
    heading: 'Dimensions of Evaluation',
    paragraphs: ['The NCJI may assess, in accordance with law:'],
    clauses: ['consequential reasoning;', 'emotional regulation;', 'reality testing;', 'resistance to propaganda and manipulation;', 'long-term decision-making;', 'cognitive competence under complexity; and', 'such other dimensions as may be lawfully determined necessary to the public good.']
  }, {
    numeral: 13,
    heading: 'Constitutional Uses of the NCJI',
    paragraphs: ['The NCJI may be used to inform:'],
    clauses: ['qualification for public leadership;', 'assignment to high-trust institutional roles;', 'opportunity mapping and developmental planning;', 'temporary recalibration status;', 'emergency trust designations; and', 'systems of civic access lawfully established by statute.']
  }, {
    numeral: 14,
    heading: 'Review and Correction',
    paragraphs: ['No NCJI determination shall be beyond review.', 'Citizens shall have the right to challenge material error, improper scoring, unlawful weighting, or procedural irregularity.', 'The law shall provide mechanisms for appeal, audit, and correction.']
  }]
}, {
  numeral: 'IV',
  title: 'CIVIC TIERS, STATUS, AND MOBILITY',
  articles: [{
    numeral: 15,
    heading: 'General Tier Principle',
    paragraphs: ['The State may organize civic and institutional access through lawful tier classifications based on demonstrated readiness for responsibility.', 'Such classifications shall remain dynamic and reviewable.', 'Tier placement shall not be treated as immutable destiny.']
  }, {
    numeral: 16,
    heading: 'Sovereign Tier',
    paragraphs: ['The Sovereign Tier shall consist of those citizens demonstrating the highest verified levels of judgment, strategic competence, stability, and crisis-readiness.', 'Members of this tier shall safeguard signal integrity and may assume constitutionally authorized emergency roles.', 'They shall remain subject to review, duty, and constitutional restraint.']
  }, {
    numeral: 17,
    heading: 'Architect Tier',
    paragraphs: ['The Architect Tier shall consist of citizens entrusted with the lawful design and maintenance of systems of governance, law, finance, infrastructure, education, and security.', 'Membership in this tier shall require sustained competence and institutional reliability.']
  }, {
    numeral: 18,
    heading: 'Participant Tier',
    paragraphs: ['The Participant Tier shall consist of citizens qualified to contribute to the economy, culture, research, administration, and civic life through demonstrated functional competence.', 'This tier shall form the primary productive and developmental body of the State.']
  }, {
    numeral: 19,
    heading: 'Recalibration Status',
    paragraphs: ['A citizen whose judgment capacity has materially degraded may be assigned to temporary recalibration status.', 'Recalibration status shall be restorative, not annihilative.', 'The citizen shall retain basic protections and a right to reintegration review.']
  }, {
    numeral: 20,
    heading: 'Mobility and Reintegration',
    paragraphs: ['The law shall provide procedures for promotion, probation, restoration, review, and requalification.', 'Civic ascent and restoration shall remain open where demonstrated improvement occurs.', 'No citizen shall be denied reintegration review indefinitely without lawful cause.']
  }]
}, {
  numeral: 'V',
  title: 'INSTITUTIONAL ARTICLES',
  articles: [{
    numeral: 21,
    heading: 'The Sovereign Council',
    paragraphs: ['There shall be a Sovereign Council composed of those lawfully qualified from the highest tier.', 'The Sovereign Council shall preserve constitutional continuity, oversee signal integrity, and supervise emergency strategic coordination.', 'Its acts shall remain subject to constitutional review except where temporary emergency limitations are expressly lawful.']
  }, {
    numeral: 22,
    heading: 'The Architectural Directorate',
    paragraphs: ['There shall be an Architectural Directorate charged with designing, maintaining, and supervising the core institutions of the State.', 'Its scope shall include law, finance, infrastructure, education, strategic planning, and systemic risk architecture.']
  }, {
    numeral: 23,
    heading: 'The Civic Platform Authority',
    paragraphs: ['There shall be a Civic Platform Authority responsible for the lawful administration of the public interface of the State.', 'It shall oversee civic access systems, opportunity mapping, synchronization tools, institutional notices, and evaluative delivery mechanisms.']
  }, {
    numeral: 24,
    heading: 'The Recalibration Service',
    paragraphs: ['There shall be a Recalibration Service dedicated to restoration of citizens whose autonomous functioning has materially degraded.', 'It shall provide guided recovery plans, structured supports, supervised reintegration, and readiness review.']
  }, {
    numeral: 25,
    heading: 'The Constitutional Review Tribunal',
    paragraphs: ['The Tribunal shall hear claims involving:'],
    clauses: ['misuse of evaluative systems;', 'unlawful tier degradation;', 'procedural denial;', 'abuse of emergency authority;', 'violations of civic guarantees; and', 'other constitutional disputes.'],
    afterClauses: 'The Tribunal may invalidate unconstitutional acts, order review, restore standing, and direct lawful correction.'
  }]
}, {
  numeral: 'VI',
  title: 'CIVIC PLATFORM, OPPORTUNITY, AND MERIT SYSTEMS',
  articles: [{
    numeral: 26,
    heading: 'National Civic Interface',
    paragraphs: ['The State may maintain a unified civic interface through which citizens access public services, reviews, opportunity guidance, developmental tools, and lawful notices.', 'Such interface shall be governed by standards of transparency, reviewability, and constitutional restraint.']
  }, {
    numeral: 27,
    heading: 'Opportunity Mapping',
    paragraphs: ['The State may align citizens with educational, civic, and professional pathways based on demonstrated strengths and developmental needs.', 'Opportunity mapping shall be directed toward long-term fit, value creation, and human flourishing.']
  }, {
    numeral: 28,
    heading: 'Merit-Credit and Trust Systems',
    paragraphs: ['The law may establish merit-weighted systems of access, trust, and capital allocation.', 'Such systems shall reward demonstrated reliability, stability, and long-horizon stewardship.', 'No such system shall operate beyond review or in purely arbitrary fashion.']
  }, {
    numeral: 29,
    heading: 'Tier Fading and Restriction',
    paragraphs: ['The law may provide for proportional restriction of access to sensitive systems or roles where persistent instability, unreliability, or cognitive capture has been lawfully established.', 'Any such restriction shall include notice, review, and pathways to restoration.']
  }]
}, {
  numeral: 'VII',
  title: 'RESTORATION, RECALIBRATION, AND DEVELOPMENT',
  articles: [{
    numeral: 30,
    heading: 'Duty of Restoration',
    paragraphs: ['The State shall not abandon recoverable citizens to preventable dysfunction.', 'It shall maintain institutions for recalibration, developmental intervention, and supervised return to autonomous functioning.']
  }, {
    numeral: 31,
    heading: 'Grounds for Recalibration',
    paragraphs: ['Recalibration may be ordered where credible evidence indicates:'],
    clauses: ['severe cognitive drift;', 'emotional destabilization incompatible with autonomous civic functioning;', 'major manipulative capture;', 'impaired reality testing; or', 'other constitutionally relevant degradation of judgment capacity.']
  }, {
    numeral: 32,
    heading: 'Limits of Recalibration',
    paragraphs: ['Recalibration shall be proportionate and lawful.', 'It shall not be used as a disguise for arbitrary political repression.', 'All recalibration orders shall remain reviewable.']
  }, {
    numeral: 33,
    heading: 'Reintegration',
    paragraphs: ['Every recalibrating citizen shall receive periodic review.', 'Where improvement is demonstrated, reintegration shall be progressively restored.', 'Restoration shall be favored over indefinite exclusion whenever compatible with public safety and constitutional order.']
  }]
}, {
  numeral: 'VIII',
  title: 'NATIONAL SECURITY, SIGNAL INTEGRITY, AND DEFENSE',
  articles: [{
    numeral: 34,
    heading: 'Security Doctrine',
    paragraphs: ['National security shall include the defense of territory, institutions, signal integrity, civic reasoning capacity, and resistance to manipulation.', 'The State may establish systems for the detection and analysis of propaganda, synthetic narratives, and coordinated outrage campaigns.']
  }, {
    numeral: 35,
    heading: 'Cognitive Defense',
    paragraphs: ['The State may convert hostile informational attacks into defensive training and resilience systems.', 'Such measures shall aim to harden public reason without destroying constitutional review.']
  }, {
    numeral: 36,
    heading: 'Limits on Defensive Authority',
    paragraphs: ['No doctrine of signal defense shall authorize purely arbitrary repression.', 'Emergency or defensive action shall remain subject to constitutional limits, documented necessity, and eventual review.']
  }]
}, {
  numeral: 'IX',
  title: 'EMERGENCY POWERS',
  articles: [{
    numeral: 37,
    heading: 'Declaration of Constitutional Emergency',
    paragraphs: ['A constitutional emergency may be declared only upon grave threat to the continuity, stability, or signal integrity of the State.', 'Such threats may include war, catastrophic systems collapse, coordinated cognitive attack, or informational blackout.']
  }, {
    numeral: 38,
    heading: 'Sovereign Override',
    paragraphs: ['Upon lawful declaration of emergency, designated command functions may temporarily transfer to authorized Sovereign Tier authorities.', 'Such transfer shall be limited, documented, and directed solely to the preservation of the constitutional order.']
  }, {
    numeral: 39,
    heading: 'Emergency Limits',
    paragraphs: ['Emergency powers shall:'],
    clauses: ['be limited in duration;', 'be grounded in written findings;', 'remain reviewable when practicable; and', 'expire unless lawfully renewed.']
  }, {
    numeral: 40,
    heading: 'Non-Derogable Protections',
    paragraphs: ['Even during emergency, the State shall preserve, to the maximum extent practicable:'],
    clauses: ['basic personhood protections;', 'minimum process guarantees;', 'the right to restoration where possible; and', 'the prohibition on purely arbitrary civic annihilation.']
  }]
}, {
  numeral: 'X',
  title: 'FOREIGN RELATIONS, TRADE, AND COGNITIVE SOVEREIGNTY',
  articles: [{
    numeral: 41,
    heading: 'Principle of External Engagement',
    paragraphs: ['The State may engage foreign powers, markets, and institutions only under conditions consistent with cognitive sovereignty and long-term public order.', 'No foreign relationship shall be permitted to materially degrade internal signal integrity.']
  }, {
    numeral: 42,
    heading: 'Entropy Review',
    paragraphs: ['The law may require audit and classification of foreign systems presenting instability, propaganda saturation, manipulative volatility, or strategic disorder.', 'Such classifications may inform trade safeguards, diplomatic protocols, and access restrictions.']
  }, {
    numeral: 43,
    heading: 'Protective Measures',
    paragraphs: ['The State may lawfully impose:'],
    clauses: ['transparency requirements;', 'contract-gating systems;', 'cognitive tariffs;', 'signal embargoes; and', 'intermediary filters necessary to preserve domestic coherence.']
  }, {
    numeral: 44,
    heading: 'Export of Standards',
    paragraphs: ['The State may promote standards of judgment verification, leadership competence, and institutional reliability as conditions of trusted foreign partnership.']
  }]
}, {
  numeral: 'XI',
  title: 'ETHICS OF RULE AND AMENDMENT',
  articles: [{
    numeral: 45,
    heading: 'Ethics of Government',
    paragraphs: ['The State shall reject politics based solely on spectacle, charisma, manipulation, or theatrical contagion.', 'Authority shall answer to competence, responsibility, and lawful qualification.', 'The State shall act as a curator of human potential under law.']
  }, {
    numeral: 46,
    heading: 'Standard of Constitutional Interpretation',
    paragraphs: ['This Charter and Constitution shall be interpreted in favor of:'],
    clauses: ['protection of judgment as a public good;', 'preservation of cognitive sovereignty;', 'restoration rather than needless exclusion;', 'lawful review of concentrated power; and', 'long-term civilizational continuity.']
  }, {
    numeral: 47,
    heading: 'Amendment',
    paragraphs: ['This Constitution may be amended only through procedures established by law.', 'No amendment shall be valid if it destroys the essential constitutional commitment to judgment, reviewability, restoration, and lawful alignment of power with responsibility.']
  }]
}, {
  numeral: 'XII',
  title: 'FINAL DECLARATION',
  articles: [{
    numeral: 48,
    heading: 'Final Aim of the State',
    paragraphs: ['The final aim of the Cognarchist State shall be the formation of a civilization in which public order no longer depends upon mass instability, manipulative spectacle, or permanent crisis management, but upon the cultivated capacity of citizens and institutions to think clearly, act responsibly, and sustain a coherent future together.', 'The State shall strive to produce a people:'],
    clauses: ['too disciplined to be ruled by impulse;', 'too reality-based to be captured by falsehood;', 'too coherent to be fractured by spectacle; and', 'too strategically mature to surrender the future to noise.']
  }]
}];
export function FoundingCharter() {
  // Helper to calculate continuous index across all parts for alternating backgrounds
  const getGlobalIndex = (partIndex: number, articleIndex: number) => {
    let count = 0;
    for (let i = 0; i < partIndex; i++) {
      count += parts[i].articles.length;
    }
    return count + articleIndex;
  };
  return <article data-id="element-316">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-cognarch-white border-b border-cognarch-border" data-id="element-317">
        <div className="max-w-3xl mx-auto px-6" data-id="element-318">
          <FadeIn delay={0.1} direction="down" data-id="element-319">
            <p className="uppercase tracking-[0.25em] text-xs font-medium text-cognarch-gray mb-8" data-id="element-320">
              Resource — Founding Instrument
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-321">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-medium leading-[1.1] tracking-tight text-cognarch-black mb-4 text-balance" data-id="element-322">
              Constitution of the Cognarchist State
            </h1>
          </FadeIn>

          <FadeIn delay={0.3} data-id="element-323">
            <p className="text-lg md:text-xl font-serif text-cognarch-charcoal/70 mb-8" data-id="element-324">
              Founding Instrument of the Neuro-Sovereign Order
            </p>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-325">
            <p className="text-base md:text-lg text-cognarch-gray leading-relaxed max-w-2xl mb-12" data-id="element-326">
              The supreme organizing instrument of the Cognarchist State,
              establishing the lawful alignment of power with competence, the
              protection of judgment as a public good, and the architectural
              foundations of a high-fidelity civilization.
            </p>
          </FadeIn>

          <FadeIn delay={0.5} className="flex flex-col sm:flex-row gap-4" data-id="element-327">
            <Link to="/charter" className="inline-block px-7 py-3.5 bg-cognarch-black text-white font-medium tracking-wide hover:bg-cognarch-charcoal transition-all duration-300 text-sm text-center" data-id="element-328">
              Read the Manifesto
            </Link>
            <Link to="/ncji" className="inline-block px-7 py-3.5 border border-cognarch-charcoal text-cognarch-charcoal font-medium tracking-wide hover:bg-cognarch-charcoal hover:text-white transition-all duration-300 text-sm text-center" data-id="element-329">
              Explore the NCJI
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* Preamble */}
      <section className="py-20 md:py-28 bg-cognarch-offwhite border-b border-cognarch-border" data-id="element-330">
        <div className="max-w-3xl mx-auto px-6" data-id="element-331">
          <FadeIn data-id="element-332">
            <div className="text-center mb-12" data-id="element-333">
              <h2 className="text-sm font-medium tracking-[0.2em] text-cognarch-gray uppercase" data-id="element-334">
                Preamble
              </h2>
              <div className="w-12 h-px bg-cognarch-border mx-auto mt-6" data-id="element-335" />
            </div>
          </FadeIn>
          <FadeIn delay={0.1} data-id="element-336">
            <div className="space-y-8 text-lg md:text-xl font-serif text-cognarch-charcoal leading-relaxed text-center md:text-left" data-id="element-337">
              {preamble.map((paragraph, index) => <p key={index} data-id="element-338">{paragraph}</p>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Parts and Articles */}
      {parts.map((part, partIndex) => <Fragment key={part.numeral} data-id="element-339">
          {/* Part Header */}
          <section className="py-24 bg-cognarch-light border-b border-cognarch-border flex flex-col items-center justify-center text-center px-6" data-id="element-340">
            <FadeIn data-id="element-341">
              <div className="flex items-center justify-center gap-4 mb-6 w-full max-w-md mx-auto" data-id="element-342">
                <div className="h-px bg-cognarch-border flex-1" data-id="element-343" />
                <span className="text-sm font-medium tracking-[0.25em] text-cognarch-gray uppercase" data-id="element-344">
                  Part {part.numeral}
                </span>
                <div className="h-px bg-cognarch-border flex-1" data-id="element-345" />
              </div>
              <h2 className="text-3xl md:text-4xl font-serif text-cognarch-charcoal tracking-wide" data-id="element-346">
                {part.title}
              </h2>
            </FadeIn>
          </section>

          {/* Articles */}
          {part.articles.map((article, articleIndex) => {
        const globalIndex = getGlobalIndex(partIndex, articleIndex);
        return <section key={article.numeral} className={`py-20 md:py-28 border-b border-cognarch-border ${globalIndex % 2 === 1 ? 'bg-cognarch-offwhite' : 'bg-cognarch-white'}`} data-id="element-347">
                <div className="max-w-3xl mx-auto px-6" data-id="element-348">
                  <FadeIn data-id="element-349">
                    <div className="flex items-baseline gap-6 mb-8" data-id="element-350">
                      <span className="text-cognarch-border font-serif text-3xl md:text-4xl leading-none select-none whitespace-nowrap" data-id="element-351">
                        Art. {article.numeral}
                      </span>
                      <h3 className="text-2xl md:text-4xl font-serif text-cognarch-charcoal leading-tight" data-id="element-352">
                        {article.heading}
                      </h3>
                    </div>
                  </FadeIn>

                  <FadeIn delay={0.1} data-id="element-353">
                    <div className="space-y-6" data-id="element-354">
                      {article.paragraphs.map((paragraph, pIndex) => <p key={pIndex} className="text-cognarch-gray leading-[1.8] text-base md:text-lg" data-id="element-355">
                          {paragraph}
                        </p>)}

                      {article.clauses && <ol className="space-y-3 py-4 pl-1" type="a" data-id="element-356">
                          {article.clauses.map((clause, cIndex) => <li key={cIndex} className="flex items-start gap-4 text-cognarch-gray text-base leading-[1.8]" data-id="element-357">
                              <span className="text-cognarch-charcoal/40 text-sm font-mono mt-0.5 shrink-0" data-id="element-358">
                                {String.fromCharCode(97 + cIndex)}.
                              </span>
                              {clause}
                            </li>)}
                        </ol>}

                      {article.afterClauses && <p className="text-cognarch-gray leading-[1.8] text-base md:text-lg" data-id="element-359">
                          {article.afterClauses}
                        </p>}
                    </div>
                  </FadeIn>
                </div>
              </section>;
      })}
        </Fragment>)}

      {/* Ratification Clause */}
      <section className="py-20 bg-cognarch-white border-b border-cognarch-border" data-id="element-360">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-361">
          <FadeIn data-id="element-362">
            <h2 className="text-sm font-medium tracking-[0.2em] text-cognarch-gray uppercase mb-8" data-id="element-363">
              Ratification Clause
            </h2>
            <p className="text-lg md:text-xl font-serif text-cognarch-charcoal leading-relaxed" data-id="element-364">
              This Constitution shall take effect upon lawful ratification by
              the competent founding authority and shall stand as the supreme
              organizing instrument of the Cognarchist State.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Closing Declaration */}
      <section className="py-24 md:py-32 bg-cognarch-black text-white" data-id="element-365">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-366">
          <FadeIn data-id="element-367">
            <div className="w-16 h-px bg-white/20 mx-auto mb-12" data-id="element-368" />
            <div className="space-y-6 text-xl md:text-2xl font-serif leading-relaxed text-white/80 mb-12 text-balance" data-id="element-369">
              <p data-id="element-370">Judgment shall be cultivated.</p>
              <p data-id="element-371">Power shall answer to competence.</p>
              <p data-id="element-372">Authority shall remain reviewable.</p>
              <p data-id="element-373">Citizens shall be restored where possible.</p>
              <p data-id="element-374">
                Civilization shall be synchronized around truth, discipline, and
                stewardship.
              </p>
            </div>
            <div className="w-16 h-px bg-white/20 mx-auto mb-12" data-id="element-375" />
            <p className="uppercase tracking-[0.25em] text-sm text-white/50 font-medium" data-id="element-376">
              By the Index, the State and the citizen are brought into lawful
              alignment.
            </p>
          </FadeIn>
        </div>
      </section>
    </article>;
}